import { sendBeacon } from "./send-beacon"

const defaultProperties = () => {
  if (typeof window === "undefined") return {}

  return {
    page_path: window.location.pathname,
    referrer: document?.referrer
  }
}

/**
 * Analytics Events are events that are meant to be tracked by Segment (see
 * models/analytics.rb). Segment is used by the data team to view internal analytics
 * data. This is different than `reportingEvent` which is meant to track data
 * that will be viewed by the user via reporting UI.
 *
 * == Example: tracking an internal event related to my feature
 *
 * Analytics.track("My Feature", "Viewed", { page_title: "Settings"})
 *
 * @param {string} object -  the key piece of the app or business that is being interacted with
 * @param {string} action - what is happening to the object
 * @param {object} properties - the properties of the event
 * @param {object} options - additional configuration options
 * @param {"fetch"|"beacon"} options.transport - transport to use when sending analytics (default: fetch)
 **/
function track(object, action, properties = {}, options) {
  event(`${object} ${action}`, { object, action, ...properties }, options)
}

/**
 * Analytics Events are events that are meant to be tracked by Segment (see
 * models/analytics.rb). Segment is used by the data team to view internal analytics
 * data. This is different than `reportingEvent` which is meant to track data
 * that will be viewed by the user via reporting UI.
 *
 * == Example: tracking an internal event related to my feature
 *
 * Analytics.event( "viewed-my-feature", { page_title: "Settings"})
 * @deprecated please use analytics.track
 * @param {string} name - the name of the event
 * @param {object} properties - the properties of the event
 * @param {object} options - additional configuration options
 * @param {"fetch"|"beacon"} options.transport - transport to use when sending analytics (default: fetch)
 **/
function event(name, properties = {}, options = { transport: "fetch" }) {
  // Don't send events in tests
  if (process.env.NODE_ENV === "test") return

  const data = JSON.stringify({
    name,
    properties: { ...defaultProperties(), ...properties }
  })

  const send = options?.transport === "beacon" ? sendBeacon : sendUsingFetch

  send("/ui/event", data)
}

function sendUsingFetch(url, data) {
  fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json"
    },
    body: data
  })
}

/**
 * Reporting Events are events that are meant to be viewed by the user via reporting UI.
 * This is different than `event` (Analytics.event) which is meant to be used for
 * internal tracking, data that the CK data team uses.
 *
 * Viewing the "Posts" tab on Creator Profile:
 *
 * Analytics.reportingEvent("view", {
 *   target_name: "Posts",
 *   target_id: 1234,
 *   target_type: "creator_profile_tab",
 *   source_name: "creator_profile",
 *   source_type: "newsletter",
 *   source_id: 1234,
 *   tags: ["my-tag", "my-react-component-name"]
 * })
 *
 * @param {string} name - the name of the event
 * @param {object} properties - the properties of the event
 * @return {Promise} - a promise that resolves when the event is sent
 **/
function reportingEvent(name, properties = {}) {
  const referrer =
    document.referrer == "" ? window.location.href : document.referrer

  const data = {
    name: name,
    properties: {
      referrer: referrer,
      source_path: window?.location?.pathname,
      user_agent: navigator?.userAgent,
      ...properties
    }
  }

  try {
    fetch("/ui/reporting_event", {
      method: "post",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify(data)
    })
  } catch (e) {
    // Ignore
  }
}

export default { event, reportingEvent, track }
